/**
 * Detail collapse for a partner.
 */
import * as React from 'react';
import { Building2, MapPin, Globe, Mail, Info } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { PARTNER_TYPE_CONFIG } from '@/components/partners/partner-configs';
import {
  formatCityLine,
  stripProtocol,
  formatPartnerDate,
} from '@/components/partners/partner-table-utils';
import { useDepartments } from '@/hooks/useDepartments';
import type { Partner } from '@/types/partner.types';

/** Detail body: maps stored department code via the departments catalogue (`name` already includes the code). */
function PartnerDetails({ partner }: Readonly<{ partner: Partner }>) {
  const typeConfig = PARTNER_TYPE_CONFIG;
  const { data: departments = [] } = useDepartments('FR');
  const departmentLabel = partner.department
    ? (departments.find((d) => d.code === partner.department)?.name ?? partner.department)
    : '';

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <Building2 className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">{partner.name}</h3>
              <LabelBadge
                label={typeConfig.label}
                icon={typeConfig.icon}
                variant={typeConfig.badgeVariant}
                color={typeConfig.badgeColor}
                size="sm"
              />
            </div>
            {partner.description && (
              <p className="details-view-header-subtitle">{partner.description}</p>
            )}
          </div>
        </div>
      </div>

      <div className="details-view-grid mt-6">
        <DetailsCard icon={MapPin} title="Coordonnées" colorName="color-2" colSpan={1}>
          <div className="space-y-3">
            <DetailField label="Adresse" value={partner.address} />
            <DetailField label="Ville" value={formatCityLine(partner.zipCode, partner.city)} />
            {departmentLabel && <DetailField label="Département" value={departmentLabel} />}
            <DetailField label="Pays" value={partner.country} />
          </div>
        </DetailsCard>

        <DetailsCard icon={Mail} title="Contact" colorName="color-1" colSpan={1}>
          <div className="space-y-3">
            {partner.contactEmail ? (
              <DetailField
                label="Email"
                value={
                  <a
                    href={`mailto:${partner.contactEmail}`}
                    className="text-[var(--color-2-nuance-1)] hover:underline text-sm"
                  >
                    {partner.contactEmail}
                  </a>
                }
              />
            ) : (
              <DetailField label="Email" value={undefined} />
            )}
            {partner.contactPhone ? (
              <DetailField
                label="Téléphone"
                value={
                  <a
                    href={`tel:${partner.contactPhone}`}
                    className="text-foreground hover:text-[var(--color-2-nuance-1)] text-sm"
                  >
                    {partner.contactPhone}
                  </a>
                }
              />
            ) : (
              <DetailField label="Téléphone" value={undefined} />
            )}
            {partner.website ? (
              <DetailField
                label="Site web"
                value={
                  <a
                    href={partner.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--color-2-nuance-1)] hover:underline text-sm flex items-center gap-1"
                  >
                    <Globe className="w-3 h-3" />
                    {stripProtocol(partner.website)}
                  </a>
                }
              />
            ) : (
              <DetailField label="Site web" value={undefined} />
            )}
          </div>
        </DetailsCard>

        <DetailsCard icon={Info} title="Détails" colorName="color-3" colSpan={2}>
          <div className="space-y-3">
            <DetailField label="Description" value={partner.description} />
            <DetailField label="Date de création" value={formatPartnerDate(partner.createdAt)} />
            <DetailField
              label="Dernière mise à jour"
              value={formatPartnerDate(partner.updatedAt)}
            />
          </div>
        </DetailsCard>
      </div>
    </>
  );
}

/** Details renderer passed to the partners {@link DataTable}. */
export const renderPartnerDetails = (partner: Partner): React.ReactNode => {
  return <PartnerDetails partner={partner} />;
};
